/**
 * Branding Configuration
 *
 * Centralized branding settings for ConnectPath.
 * Supports multiple branding themes (default, white-label, etc.)
 */

export interface BrandingConfig {
  // App identity
  appName: string;
  appTagline: string;

  // Logo assets
  logo: string;           // Main logo (icon only or full logo)
  logoWithText: boolean;  // Whether logo includes text or needs separate text
  logoAlt: string;        // Alt text for logo

  // Favicon and PWA icons
  favicon: string;
  pwaIcon: string;

  // Colors (for PWA manifest and meta tags)
  themeColor: string;
  backgroundColor: string;
  primaryColor: string;

  // Footer
  copyrightHolder: string;

  // URLs
  supportUrl?: string;
  websiteUrl?: string;
}

// Default ConnectPath branding
const defaultBranding: BrandingConfig = {
  appName: 'ConnectPath',
  appTagline: 'Your church assessment and growth platform',

  logo: '/logo.svg',
  logoWithText: false,  // Logo is icon-only, needs text alongside
  logoAlt: 'ConnectPath',

  favicon: '/favicon.ico',
  pwaIcon: '/logo.svg',

  themeColor: '#1890FF',
  backgroundColor: '#ffffff',
  primaryColor: '#1890FF',

  copyrightHolder: 'ConnectPath',

  supportUrl: 'https://support.connectpath.com',
  websiteUrl: 'https://connectpath.com',
};

// White label branding - NO branding, customers provide their own
// Set via environment variables:
// VITE_BRANDING=whitelabel
// VITE_APP_NAME="Your Church Name"
// VITE_APP_TAGLINE="Your tagline"
// VITE_LOGO="/your-logo.svg"
// VITE_THEME_COLOR="#yourcolor"
// VITE_COPYRIGHT="Your Organization"
const whiteLabelBranding: BrandingConfig = {
  appName: import.meta.env.VITE_APP_NAME || '',
  appTagline: import.meta.env.VITE_APP_TAGLINE || '',

  logo: import.meta.env.VITE_LOGO || '',
  logoWithText: import.meta.env.VITE_LOGO_WITH_TEXT === 'true',
  logoAlt: import.meta.env.VITE_APP_NAME || '',

  favicon: import.meta.env.VITE_FAVICON || '/favicon.ico',
  pwaIcon: import.meta.env.VITE_PWA_ICON || import.meta.env.VITE_LOGO || '',

  themeColor: import.meta.env.VITE_THEME_COLOR || '#2563eb',
  backgroundColor: import.meta.env.VITE_BG_COLOR || '#ffffff',
  primaryColor: import.meta.env.VITE_PRIMARY_COLOR || import.meta.env.VITE_THEME_COLOR || '#2563eb',

  copyrightHolder: import.meta.env.VITE_COPYRIGHT || '',
};

// Available branding configurations
const brandingConfigs: Record<string, BrandingConfig> = {
  default: defaultBranding,
  whitelabel: whiteLabelBranding,
};

// Get branding from environment or default
const getBrandingKey = (): string => {
  return import.meta.env.VITE_BRANDING || 'default';
};

// Export the active branding configuration
export const branding: BrandingConfig = brandingConfigs[getBrandingKey()] || defaultBranding;

// Helper functions
export const getLogoSrc = () => branding.logo;
export const getAppName = () => branding.appName;
export const getTagline = () => branding.appTagline;
export const showLogoText = () => !branding.logoWithText && !!branding.appName;
export const showLogo = () => !!branding.logo;
export const showTagline = () => !!branding.appTagline;
export const getFavicon = () => branding.favicon;
export const getThemeColor = () => branding.themeColor;
export const getCopyright = () => branding.copyrightHolder
  ? `© ${new Date().getFullYear()} ${branding.copyrightHolder}. All rights reserved.`
  : '';
export const isWhiteLabel = () => getBrandingKey() === 'whitelabel';

export default branding;
